import type { AppType, PageType } from '@/types';
export const ECHO_DESIGN_SYSTEM_FLAG = 'echoDesignSystem';
export const AUDIENCE_TAB_EXPERIMENTAL_FEATURES_FLAG = 'audienceTabExperimentalFeatures';
export const CONTENT_PERFORMANCE_DOWNLOAD_BUTTON_FLAG = 'contentPerformanceDownloadButton';
/**
 * When true, the selectable date range extends back to 1 January 2014 (date picker and URL
 * validation), and downloads spanning more than a year are limited to the total breakdown.
 * The key predates the decision to drop the "All time" preset, so it no longer adds one.
 * TODO: remove once `allTimePreset` is enabled for everyone, along with `useAllTimePreset`.
 */
export const ALL_TIME_PRESET_FLAG = 'allTimePreset';
export const NEW_DOWNLOAD_DRAWERS_FLAG = 'newDownloadDrawers';
/** When true, load approved Crowdin catalogs. When false, render English `defaultMessage`. */
export const CROWDIN_TRANSLATOR_FLAG = 'crowdinTranslator';

/**
 * Maximum number of networks that can be selected at once on the multi-network content performance
 * page. Mirrors the API's `MAX_NETWORK_IDS` cap for `/v2/content/*?networkIds=`.
 * @see applications/revenue-api/src/middleware/resolve-network-ids.ts
 */
export const MAX_NETWORK_IDS = 10;

export enum CampaignCategory {
  ADS = 'ads',
  SPONSORSHIPS = 'sponsorships',
  PROGRAMMATIC = 'programmatic',
  BRANDED_CONTENT = 'branded_content',
  SOCIAL = 'social',
  OTHER = 'other',
}

export const CAMPAIGN_CATEGORIES = {
  ADS: 'ads',
  SPONSORSHIPS: 'sponsorships',
  PROGRAMMATIC: 'programmatic',
  BRANDED_CONTENT: 'branded_content',
  SOCIAL: 'social',
  OTHER: 'other',
} as const;

export const AUDIENCE_PAGE = 'audience' as const satisfies PageType;
export const CONTENT_PERFORMANCE_PAGE = 'content-performance' as const satisfies PageType;

export const PAGE_TYPES: PageType[] = ['audience', CONTENT_PERFORMANCE_PAGE, 'earnings', 'payouts'];

export const PAGE_TO_APP_MAP: Record<PageType, AppType> = {
  earnings: 'revenue',
  payouts: 'revenue',
  audience: 'insights',
  [CONTENT_PERFORMANCE_PAGE]: 'insights',
};

/** Maximum number of shows that can be selected at once, per page. */
export const MAX_SHOWS_PER_PAGE: Record<PageType, number> = {
  earnings: 1,
  payouts: 1,
  audience: 1,
  [CONTENT_PERFORMANCE_PAGE]: 10,
};

/**
 * Maps app type to the default page type.
 * This is used to redirect users to the default page based on app type when they navigate to the root route.
 */
export const APP_TO_DEFAULT_PAGE_MAP: Record<AppType, PageType> = {
  revenue: 'earnings',
  insights: CONTENT_PERFORMANCE_PAGE,
};

/**
 * Maps normalized (lowercase) platform names to logo image paths.
 * The normalize name function will make the name lowercase and remove any spaces.
 * So make sure to use the normalized name for the key. (lowercase and no spaces)
 */
const PLATFORM_LOGO_MAP: Record<string, string> = {
  acast: '/social/acast.webp',
  amazonmusic: '/podcast_players/amazon_music.webp',
  applepodcasts: '/podcast_players/apple_podcasts.webp',
  castbox: '/podcast_players/castbox.webp',
  instagram: '/social/instagram.webp',
  overcast: '/podcast_players/overcast.svg',
  pocketcasts: '/podcast_players/pocket_casts.webp',
  spotify: '/podcast_players/spotify.webp',
  tiktok: '/social/tiktok.webp',
  youtube: '/social/youtube.webp',
  default: '/podcast_players/podcast-default.png',
};

const normalizePlatformName = (name: string): string =>
  name.toLowerCase().trim().replace(/\s+/g, '');

/**
 * Returns the logo image path for a given platform name.
 * Normalizes the input (lowercase, trim, collapse whitespace) before lookup,
 * so any casing or spacing variant will match.
 * Unknown names resolve to the `default` entry in the platform logo map (generic podcast icon).
 */
export const getPlatformLogoSrc = (name: string): string =>
  PLATFORM_LOGO_MAP[normalizePlatformName(name)] ?? PLATFORM_LOGO_MAP.default;

/**
 * Fixed icons for the audio overview cards.
 */
export const PODCAST_PLAYERS = ['Spotify', 'Apple Podcasts', 'Default'];

export const APPLE_PODCASTS_ONLY_PLAYER_SECTION = {
  label: 'Apple Podcasts only',
  players: ['Apple Podcasts'],
} as const;

/**
 * TODO: make this dynamic once more video players are supported.
 */
export const getApplePodcastsOnlyPlayerSections = (): Array<{
  label: string;
  players: string[];
}> => [
  {
    label: APPLE_PODCASTS_ONLY_PLAYER_SECTION.label,
    players: [...APPLE_PODCASTS_ONLY_PLAYER_SECTION.players],
  },
];
